<template>
  <suspense @resolve="onResolve">
    <slot />
  </suspense>
</template>

<script setup>
import { computedEager, useFetch, createEventHook } from '@vueuse/core';
import { reactive, ref, computed, toRefs, provide, watch, readonly } from 'vue';
import qs from 'qs';

const emit = defineEmits(['response']);

const props = defineProps({
  url: String,
  params: Object,
  immediate: {
    type: Boolean,
    default: true,
  },
  method: {
    type: String,
    default: 'GET',
  },
});

const { url, params: propsParams, immediate, method } = toRefs(props);

const overwriteParams = reactive({});
const setOverwriteParam = (key, value) => {
  overwriteParams[key] = value;
};
provide('api/setOverwriteParam', setOverwriteParam);

const combinedParams = computedEager(() => {
  const sortByKeys = obj => {
    return Object.keys(obj)
      .sort()
      .reduce((result, key) => {
        result[key] = obj[key];
        return result;
      }, {});
  };

  return sortByKeys({
    ...propsParams.value,
    ...overwriteParams,
  });
});
provide('api/params', readonly(combinedParams));

const stringifiedParams = computed(() => {
  return qs.stringify(combinedParams.value, {
    arrayFormat: 'indices',
    encode: false,
  });
});
const currentUrlWithParams = computedEager(() => {
  return `${url.value}?${stringifiedParams.value}`;
});

const prefetchScrollHeight = ref(document.scrollingElement.scrollHeight);
const lastUrl = ref(undefined);
const { isFetching, data, execute } = useFetch(
  currentUrlWithParams,
  {
    method: method.value,
  },
  {
    refetch: false,
    immediate: false,
    beforeFetch({ url, cancel }) {
      if (lastUrl.value === url) return cancel();
      prefetchScrollHeight.value = document.scrollingElement.scrollHeight;
      lastUrl.value = url;
    },
  }
).json();
provide('api/isFetching', readonly(isFetching));
provide('api/prefetchScrollHeight', readonly(prefetchScrollHeight));

// Code to provide optional delayed fetching indicator to smoothen UX
//
// const SHOW_FETCHING_DELAY = 200;
// const showFetchingTimeout = ref(undefined);
// const showFetching = ref(false);
// watch(isFetching, isFetching => {
//   if (showFetchingTimeout.value) clearTimeout(showFetchingTimeout.value);
//   if (!isFetching) return (showFetching.value = false);

//   showFetchingTimeout.value = setTimeout(
//     () => (showFetching.value = true),
//     SHOW_FETCHING_DELAY
//   );
// });
// provide('api/showFetching', readonly(showFetching));

const onResolve = () => {
  watch(currentUrlWithParams, () => execute(), { immediate: immediate.value });
};

const responseHistory = ref([]);
watch(data, data => {
  if (!data) return;
  emit('response', data);
  responseHistory.value.push(data);
});
provide('api/responseHistory', readonly(responseHistory));

const clearResponseHistoryEvent = createEventHook();
const clearResponseHistory = () => {
  if (responseHistory.value.length === 0) return;
  responseHistory.value = [];
  clearResponseHistoryEvent.trigger();
};
provide('api/clearResponseHistory', clearResponseHistory);
provide('api/onClearResponseHistory', clearResponseHistoryEvent.on);

const lastResponse = computed(() => {
  return responseHistory.value[responseHistory.value.length - 1];
});
provide('api/lastResponse', readonly(lastResponse));
</script>
